import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ImportContainer } from "../../../../../Model/dataProcess/importContainer";
import { WawiListService } from "../../../../../Services/wawiList.service";
import { LoginService } from "../../../../../Services/login.service";
import { lastValueFrom } from "rxjs";
import { WawiList, WawiListElement } from "../../../../../Model/wawi/WawiList";
import { DxTreeViewComponent } from "devextreme-angular/ui/tree-view";
import { UpdateTemplateService } from "../../../../../Services/updateTemplate.service";
import { TranslateService } from "@ngx-translate/core";
import { FIELD_LIST, GROUP_LIST } from "../vaildUnitMappingFields";
import { UnitMappingService } from "../../../../../Services/DataProcess/unitMapping.service";
import { SystemService } from "../../../../../Services/system.service";
import { DxDataGridComponent } from "devextreme-angular";

type Item = {
  id: number;
  parentId: number | null;
  field: string;
  translatedField?: string;
};

@Component({
  selector: 'np-unit-mapping-tab',
  templateUrl: './np-unit-mapping-tab.component.html',
  styleUrl: './np-unit-mapping-tab.component.css',
})
export class NpUnitMappingTabComponent implements OnInit {
  @ViewChild('tree', { static: false }) tree?: DxTreeViewComponent;
  @ViewChild('mappingGrid', { static: false }) mappingGrid?: DxDataGridComponent;
  @Input() container!: ImportContainer;

  protected importFields: Array<any> = [];
  protected mappingList: Array<WawiList> = [];
  protected newMappingList: WawiList;
  protected selectedMappingList: WawiList;
  protected addMappingListVisible: boolean = false;

  protected selectedImportFieldId: number;

  protected listSelected: boolean = false;
  protected selectedMappingId: number;
  protected selectedImportFieldsValues: WawiListElement[] = [];
  protected mappings: WawiListElement[] = [];

  private readonly MAX_SELECTED = 7;
  private itemById = new Map<number, Item>();

  protected attachRadioItems = [
    { text: this.translate.instant("Feld spezifisch"), value: "field" },
    { text: this.translate.instant("Manuell"), value: "manually" }
  ];

  protected validityRadioItems = [
    { text: this.translate.instant("Global"), value: "global" },
    { text: this.translate.instant("Container spezifisch"), value: "container" }
  ];

  protected nullValueRadioItems = [
    { text: this.translate.instant("Nicht anlegen"), value: "apply" },
    { text: this.translate.instant("Anlegen"), value: "notApply" }
  ];

  protected radioAttachValue: string = "field";
  protected radioValidityValue: string = "global";
  protected radioNullValueValue: string = "apply";

  private syncing = false;
  private prevSelectedKeys: number[] = [];

  private autoMappedIds = new Set<number>();

  dataSource: Item[] = [];
  treeData: Item[] = [];
  selectedKeys: number[] = [];

  constructor(
    public wawiListService: WawiListService,
    public loginService: LoginService,
    public updateTemplateService: UpdateTemplateService,
    public translate: TranslateService,
    public unitMappingService: UnitMappingService,
    public systemService: SystemService,
  ) {
    this.addMappingList = this.addMappingList.bind(this);
  }

  async ngOnInit() {
    await this.loadMappingList();
    this.fillDataSource();
  }

  get key() {
    if (this.selectedMappingList == undefined)
      return this.translate.instant("Schlüssel");
    return this.selectedMappingList.key;
  }

  get value() {
    if (this.selectedMappingList == undefined)
      return this.translate.instant("Wert");
    return this.selectedMappingList.value;
  }

  dropDownCfg = {
    toolbarItems: [
      {
        widget: 'dxButton',
        location: 'after',
        options: {
          icon: 'plus',
          stylingMode: 'outlined',
          hint: this.translate.instant('Mappingliste hinzufügen'),
          onClick: () => this.addNewMappingList()
        }
      }
    ]
  };

  fillDataSource() {
    let nextId = 1;
    const groupIdByName = new Map<string, number>();

    this.dataSource = GROUP_LIST.map(g => {
      const id = nextId++;
      groupIdByName.set(g, id);
      return { id: id, parentId: null, field: g, translatedField: this.translate.instant(g) } as Item;
    });

    FIELD_LIST.forEach(f => {
      const parentId = groupIdByName.get(f.group);
      if (parentId != null) {
        this.dataSource.push({
          id: nextId++,
          parentId: parentId,
          field: f.field,
          translatedField: this.translate.instant(f.field)
        } as Item);
      }
    });

    this.treeData = this.dataSource;
    this.itemById = new Map(this.dataSource.map(x => [x.id, x]));
  }

  private async loadMappingList() {
    this.selectedMappingList = null;
    this.selectedMappingId = undefined;

    let containerId = this.radioValidityValue == "container" ? this.container.id : "";
    this.mappingList = await lastValueFrom(
      this.wawiListService.getAllListsWithoutFields(this.loginService.currentCustomer.id, containerId)
    );

    if (this.radioValidityValue == "global") {
      this.mappingList = this.mappingList.filter(x => !x.containerId);
    }

    this.rebuildMappings();
  }

  protected onImportFieldSelectChanged($event: any) {}

  protected async onMappingListSelectChanged($event: any) {
    this.listSelected = false;

    let selectedMappingList = this.mappingList.find(x => x.id == $event.value);
    this.selectedMappingList = await lastValueFrom(
      this.wawiListService.getList(
        selectedMappingList.id,
        this.loginService.currentCustomer.id,
        selectedMappingList.containerId
      )
    );

    this.listSelected = !!this.selectedMappingList;
    this.rebuildMappings(true);

    this.autoMappedIds.clear();
  }

  onTreeContentReady(e: any) {
    const tree = e.component;
    this.selectedKeys.filter(k => this.isChild(k)).forEach(k => tree.selectItem(k));
    this.prevSelectedKeys = [...this.selectedKeys];
  }

  private isChild(id: number): boolean {
    return (this.itemById.get(id)?.parentId ?? null) != null;
  }

  get selectedChildCount(): number {
    return this.selectedKeys.filter(k => this.isChild(k)).length;
  }

  isSelected(id: number): boolean {
    return this.selectedKeys.includes(id);
  }

  isLeafDisabled(id: number): boolean {
    if (!this.isChild(id))
      return true;
    if (this.isSelected(id))
      return false;
    return this.selectedChildCount >= this.MAX_SELECTED;
  }

  private enforceChildLimit(allKeys: number[], oldAllKeys: number[]): number[] {
    const childKeys = allKeys.filter(k => this.isChild(k));
    if (childKeys.length <= this.MAX_SELECTED)
      return allKeys;

    const oldChildKeys = oldAllKeys.filter(k => this.isChild(k));
    const addedChild = childKeys.filter(k => !oldChildKeys.includes(k));

    let keep = new Set<number>(allKeys);
    let excess = childKeys.length - this.MAX_SELECTED;

    for (let i = addedChild.length - 1; i >= 0 && excess > 0; i--) {
      keep.delete(addedChild[i]);
      excess--;
    }

    if (excess > 0) {
      for (let i = childKeys.length - 1; i >= 0 && excess > 0; i--) {
        const k = childKeys[i];
        if (keep.has(k)) {
          keep.delete(k);
          excess--;
        }
      }
    }

    return allKeys.filter(k => keep.has(k));
  }

  onTreeItemClick(e: any) {
    const item: Item | undefined = e.itemData;
    if (!item)
      return;

    if (item.parentId == null) {
      const node = e.node;
      if (node?.expanded)
        e.component.collapseItem(item.id);
      else
        e.component.expandItem(item.id);
    }
  }

  onLeafCheckChanged(id: number, checked: boolean) {
    const tree = this.tree?.instance;
    if (!tree)
      return;

    if (checked) {
      const currentChildKeys = tree.getSelectedNodeKeys().filter((k: number) => this.isChild(k));
      if (currentChildKeys.length >= this.MAX_SELECTED && !currentChildKeys.includes(id))
        return;
      tree.selectItem(id);
    } else {
      tree.unselectItem(id);
    }
  }

  async onTreeSelectionChanged(e: any) {
    if (this.syncing)
      return;
    this.syncing = true;

    const tree = e.component;
    const oldKeys = [...this.prevSelectedKeys];

    let keys: number[] = tree.getSelectedNodeKeys().filter((k: number) => this.isChild(k));

    const limited = this.enforceChildLimit(keys, oldKeys);
    if (limited.length !== keys.length) {
      const removed = keys.filter(k => !limited.includes(k));
      removed.forEach(k => tree.unselectItem(k));
      keys = tree.getSelectedNodeKeys().filter((k: number) => this.isChild(k));
    }

    this.selectedKeys = keys;
    this.prevSelectedKeys = [...keys];

    await this.loadSelectedImportFieldsValues();
    this.syncing = false;
  }

  async onTagsChanged(e: any) {
    if (this.syncing)
      return;
    this.syncing = true;

    const oldKeys = [...this.prevSelectedKeys];
    let newKeys: number[] = e.value ?? [];
    newKeys = newKeys.filter(k => this.isChild(k));

    const limited = this.enforceChildLimit(newKeys, oldKeys);
    if (limited.length !== newKeys.length)
      newKeys = limited;

    const added = newKeys.filter(k => !oldKeys.includes(k));
    const removed = oldKeys.filter(k => !newKeys.includes(k));

    const tree = this.tree?.instance;
    if (tree) {
      added.forEach(k => tree.selectItem(k));
      removed.forEach(k => tree.unselectItem(k));
    }

    this.selectedKeys = newKeys;
    this.prevSelectedKeys = [...newKeys];

    await this.loadSelectedImportFieldsValues();
    this.syncing = false;
  }

  protected getSelectedImportFieldNames(): string[] {
    return this.selectedKeys
      .map(id => this.itemById.get(id))
      .filter((x): x is Item => !!x && x.parentId != null)
      .map(x => x.field);
  }

  protected async loadSelectedImportFieldsValues() {
    const selectedFields = this.getSelectedImportFieldNames();
    this.selectedImportFieldsValues = [];

    if (selectedFields.length > 0) {
      const values =
        await this.unitMappingService.getUnitMappingValues(
          this.container.activeCatalogId,
          this.loginService.currentCustomer.id,
          selectedFields
        );

      let nextId =
        (this.mappings?.reduce((max, x) => Math.max(max, Number((x as any).id) || 0), 0) ?? 0) + 1;

      values.forEach(v => this.selectedImportFieldsValues.push(new WawiListElement(nextId++, "", v)));
    }

    this.rebuildMappings();
  }

  protected async validityRadioChanged(e: any) {
    this.radioValidityValue = e.value;
    await this.loadMappingList();
  }

  protected onMappingListFieldChanged() {}

  protected attachRadioChanged(e: any) {
    if (e.value == 'manually') {
      this.selectedKeys = [];
      this.selectedImportFieldsValues = [];
      this.rebuildMappings();
    }
  }

  private addNewMappingList() {
    this.newMappingList = new WawiList("", "", "", "", true, [], true);
    this.addMappingListVisible = true;
  }

  addMappingList() {
    if (this.newMappingList.listName == "") {
      this.systemService.notifyInfo(this.translate.instant("Bitte eine Listennamen angeben!"));
      return;
    }
    if (this.newMappingList.value == "" || (this.newMappingList.key == "" && this.newMappingList.isMapping)) {
      this.systemService.notifyInfo(this.translate.instant("Bitte Spaltennamen angeben!"));
      return;
    }

    if (this.radioValidityValue == "container") {
      this.newMappingList.containerId = this.container.id;
    }

    this.systemService.notifyInfo(this.translate.instant("Liste wird erstellt"));
    this.wawiListService.create(this.newMappingList, this.loginService.currentCustomer.id).subscribe(() => {
      this.systemService.notifyInfo(this.translate.instant("Liste wurde erstellt"));
      this.loadMappingList();
    });

    this.addMappingListVisible = false;
  }

  private rebuildMappings(mappingListChanged: boolean = false) {
    const listFields = this.selectedMappingList?.fields ?? [];
    let importFields = this.selectedImportFieldsValues ?? [];

    const listValues = new Set(listFields.map(x => x.value));
    importFields = importFields.filter(f => !listValues.has(f.value));

    if (mappingListChanged && importFields.length > 0) {
      let nextId =
        (listFields?.reduce((max, x) => Math.max(max, Number((x as any).id) || 0), 0) ?? 0) + 1;
      importFields.forEach(f => f.id = nextId++);
    }

    this.mappings = [...listFields, ...importFields];
  }

  runAutoMapping() {
    if (!this.listSelected || !this.selectedMappingList)
      return;

    const listFields = this.selectedMappingList.fields ?? [];
    const rawKeys = listFields
      .map(x => (x.key ?? '').toString().trim())
      .filter(k => k.length > 0);

    const keyCache = rawKeys.map(k => ({ raw: k, norm: this.norm(k) }));

    this.autoMappedIds.clear();
    const filledIds: number[] = [];

    for (const row of this.mappings) {
      if ((row.key ?? '').toString().trim().length > 0)
        continue;

      const valueRaw = (row.value ?? '').toString().trim();
      if (!valueRaw)
        continue;

      const v = this.norm(valueRaw);
      if (!v)
        continue;

      if (v.length < 2)
        continue;

      const match = keyCache.find(k => k.norm.includes(v));
      if (!match)
        continue;

      row.key = match.raw;
      this.autoMappedIds.add(row.id);
      filledIds.push(row.id);
    }

    if (!filledIds.length) {
      this.systemService.notifyInfo(this.translate.instant("Keine passenden Treffer gefunden"));
      return;
    }

    this.mappings = [...this.mappings];

    const grid = this.mappingGrid?.instance;
    if (grid)
      grid.refresh();

    this.systemService.notifyInfo(this.translate.instant("Einträge automatisch gemappt", { count: filledIds.length }));
    this.onSaveMappings();
  }

  onRowPrepared(e: any) {
    if (e.rowType !== 'data')
      return;

    if (this.autoMappedIds.has(e.key)) {
      e.rowElement.classList.add('auto-mapped-row');
    } else {
      e.rowElement.classList.remove('auto-mapped-row');
    }
  }

  onRowInserting(e: any) {
    const maxId = (this.mappings?.reduce((m, x) => Math.max(m, Number(x.id) || 0), 0) ?? 0);
    e.data.id = maxId + 1;
  }

  onSaveMappings() {
    this.selectedMappingList.fields = this.mappings.filter(f => (f.key ?? '').toString().trim() !== "");
    this.wawiListService.update(this.selectedMappingList, this.loginService.currentCustomer.id).subscribe(() => {});
  }

  private norm(s: any): string {
    return (s ?? '')
      .toString()
      .trim()
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
